import { useEffect, useMemo, useRef, useState } from "react";
import { LngLatBounds, Map as MLMap, NavigationControl, type GeoJSONSource, type MapLayerMouseEvent } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { buildOsmStyle, MAP_CONTAINER_BACKGROUND } from "../lib/mapStyle";
import { navigate } from "../lib/router";
import { focusData, mapData, type GraphModel, type VisibleGraph } from "../lib/neighbourGraph";
import { NodeTip } from "./NeighbourGraphTip";

// The Neighbour graph's Map layout: the same MapLibre base map and tile style as the Live Map (lib/mapStyle.ts, following
// the app theme), with the shown nodes as a circle layer and the shown links as a line layer. Built fresh when the theme
// changes (the parent keys it by theme). The hover highlight, click to the node page and fly-to for the search match the
// graph layout.

const EDGE_SOURCE = "ng-edges";
const NODE_SOURCE = "ng-nodes";
const VEIL_SOURCE = "ng-veil";
const FOCUS_EDGES = "ng-focus-edges";
const FOCUS_NODES = "ng-focus-nodes";
const EMPTY = { type: "FeatureCollection", features: [] };
const RADIUS = ["interpolate", ["linear"], ["zoom"], 4, ["*", ["get", "r"], 0.75], 9, ["*", ["get", "r"], 1.5]];
const FLY_ZOOM = 8;

function cssVar(el: Element, name: string, fallback: string): string {
  const v = getComputedStyle(el).getPropertyValue(name).trim();
  return v || fallback;
}

export function NeighbourGraphMap({
  model, vis, theme, colorOf, selected, height,
}: {
  model: GraphModel;
  vis: VisibleGraph;
  theme: "light" | "dark";
  colorOf: (role: string | null) => string;
  selected: number | null;
  height: number;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MLMap | null>(null);
  const ready = useRef(false);
  const userMoved = useRef(false);
  const live = useRef({ model, vis });
  live.current = { model, vis };
  const [hover, setHover] = useState<number | null>(null);
  const hoverRef = useRef<number | null>(null);
  const data = useMemo(() => mapData(model, vis, colorOf), [model, vis, colorOf]);
  const dataRef = useRef(data);
  dataRef.current = data;

  // --- build the map once (per theme) ----------------------------------------------------------------------------
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const map = new MLMap({
      container: el,
      style: buildOsmStyle(theme),
      center: [-114, 44],
      zoom: 4,
      attributionControl: { compact: true },
      canvasContextAttributes: { preserveDrawingBuffer: true },
    });
    mapRef.current = map;
    ready.current = false;
    map.addControl(new NavigationControl({ showCompass: false }), "top-right");
    map.on("movestart", (e) => {
      if (e.originalEvent) userMoved.current = true;
    });
    const init = () => {
      const ink = cssVar(el, "--ink", "#111");
      const inkSecondary = cssVar(el, "--ink-secondary", "#555");
      const surface = cssVar(el, "--surface", "#fff");
      map.addSource(EDGE_SOURCE, { type: "geojson", data: dataRef.current.edges as never });
      map.addSource(NODE_SOURCE, { type: "geojson", data: dataRef.current.nodes as never });
      map.addLayer({
        id: "ng-edges", type: "line", source: EDGE_SOURCE,
        layout: { "line-cap": "round" },
        paint: { "line-color": inkSecondary, "line-width": ["get", "w"], "line-opacity": ["get", "o"] },
      });
      map.addLayer({
        id: "ng-nodes", type: "circle", source: NODE_SOURCE,
        paint: {
          "circle-color": ["get", "color"],
          "circle-radius": RADIUS as never,
          "circle-stroke-width": 1,
          "circle-stroke-color": surface,
          "circle-opacity": 1,
        },
      });
      // a veil over the map and the big layers: a hover dims everything below it by changing one number, so the
      // thousands of links are not re-drawn from new styling; the highlight is drawn above it
      map.addSource(VEIL_SOURCE, {
        type: "geojson",
        data: { type: "Feature", properties: {}, geometry: { type: "Polygon", coordinates: [[[-180, -85], [180, -85], [180, 85], [-180, 85], [-180, -85]]] } } as never,
      });
      map.addLayer({ id: "ng-veil", type: "fill", source: VEIL_SOURCE, paint: { "fill-color": MAP_CONTAINER_BACKGROUND[theme], "fill-opacity": 0 } });
      // the highlight lives in its own small sources, swapped in on every hover; the big layers are only dimmed
      map.addSource(FOCUS_EDGES, { type: "geojson", data: EMPTY as never });
      map.addSource(FOCUS_NODES, { type: "geojson", data: EMPTY as never });
      map.addLayer({
        id: "ng-focus-edges", type: "line", source: FOCUS_EDGES,
        layout: { "line-cap": "round" },
        paint: { "line-color": ink, "line-width": ["max", 1.4, ["get", "w"]], "line-opacity": ["max", 0.6, ["get", "o"]] },
      });
      map.addLayer({
        id: "ng-focus-nodes", type: "circle", source: FOCUS_NODES,
        paint: {
          "circle-color": ["get", "color"],
          "circle-radius": RADIUS as never,
          "circle-stroke-width": ["case", ["get", "focus"], 2.5, 1],
          "circle-stroke-color": ["case", ["get", "focus"], ink, surface],
        },
      });
      ready.current = true;
      fit(map);
      applyFocus(map, hoverRef.current ?? selectedRef.current);
    };
    // the style is an object, so it is ready at once or on the next tick; waiting for "load" would wait for every tile
    if (map.isStyleLoaded()) init();
    else map.once("style.load", init);
    map.on("mousemove", "ng-nodes", (e: MapLayerMouseEvent) => {
      const f = e.features?.[0];
      if (!f) return;
      map.getCanvas().style.cursor = "pointer";
      const idx = Number(f.properties?.i);
      if (idx !== hoverRef.current) {
        hoverRef.current = idx;
        setHover(idx);
      }
      const tip = tipRef.current;
      if (tip) {
        const w = el.clientWidth;
        tip.style.left = `${e.point.x + 16 + 300 > w ? Math.max(4, e.point.x - 16 - tip.offsetWidth) : e.point.x + 16}px`;
        tip.style.top = `${Math.max(4, e.point.y - 8)}px`;
      }
    });
    map.on("mouseleave", "ng-nodes", () => {
      map.getCanvas().style.cursor = "";
      hoverRef.current = null;
      setHover(null);
    });
    map.on("click", "ng-nodes", (e: MapLayerMouseEvent) => {
      const f = e.features?.[0];
      if (f) navigate(`#/nodes/${encodeURIComponent(live.current.model.nodes[Number(f.properties?.i)].id)}`);
    });
    return () => {
      ready.current = false;
      map.remove();
      mapRef.current = null;
    };
    // the map is rebuilt only for a new theme; data and focus updates go through the effects below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);

  const selectedRef = useRef<number | null>(selected);
  selectedRef.current = selected;

  function fit(map: MLMap) {
    const b = dataRef.current.bounds;
    if (!b) return;
    const bounds = new LngLatBounds([b[0], b[1]], [b[2], b[3]]);
    map.fitBounds(bounds, { padding: 48, maxZoom: 9, duration: 0 });
  }

  function applyFocus(map: MLMap, focus: number | null) {
    const edgeSrc = map.getSource(FOCUS_EDGES) as GeoJSONSource | undefined;
    const nodeSrc = map.getSource(FOCUS_NODES) as GeoJSONSource | undefined;
    if (!edgeSrc || !nodeSrc) return;
    if (focus === null) {
      edgeSrc.setData(EMPTY as never);
      nodeSrc.setData(EMPTY as never);
      map.setPaintProperty("ng-veil", "fill-opacity", 0);
    } else {
      const f = focusData(dataRef.current, live.current.vis.edges, focus);
      edgeSrc.setData(f.edges as never);
      nodeSrc.setData(f.nodes as never);
      map.setPaintProperty("ng-veil", "fill-opacity", 0.8);
    }
  }

  // --- data: the shown nodes and links ---------------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready.current) return;
    (map.getSource(EDGE_SOURCE) as GeoJSONSource | undefined)?.setData(data.edges as never);
    (map.getSource(NODE_SOURCE) as GeoJSONSource | undefined)?.setData(data.nodes as never);
    if (!userMoved.current) fit(map);
    applyFocus(map, hoverRef.current ?? selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // --- hover / selection highlight ---------------------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (map && ready.current) applyFocus(map, hover ?? selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hover, selected, vis]);

  // --- search: fly to the picked node --------------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map || selected === null) return;
    const n = live.current.model.nodes[selected];
    if (n.lat === null || n.lon === null) return;
    const go = () => map.flyTo({ center: [n.lon as number, n.lat as number], zoom: Math.max(map.getZoom(), FLY_ZOOM), duration: 700 });
    if (ready.current) go();
    else map.once("style.load", go);
  }, [selected]);

  // --- size ----------------------------------------------------------------------------------------------------
  useEffect(() => {
    mapRef.current?.resize();
  }, [height]);

  return (
    <div className="ng-map" style={{ height, background: MAP_CONTAINER_BACKGROUND[theme] }}>
      <div ref={containerRef} className="ng-map-canvas" />
      <div className="ng-tip" ref={tipRef} style={{ display: hover === null ? "none" : "block" }}>
        {hover !== null && <NodeTip model={model} vis={vis} idx={hover} />}
      </div>
    </div>
  );
}
